/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import swal from "sweetalert";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDProgress from "components/MDProgress";
import MDInput from "components/MDInput";
import MDDropzone from "components/MDDropzone";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD, API_CHAT } from "utils";
import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import ChatIcon from "@mui/icons-material/Chat";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import CircularProgress from "@mui/material/CircularProgress";
// Status colors
const STATUS_COLORS = {
  pending: "warning",
  in_progress: "info",
  completed: "success",
  rejected: "error",
};

function DaftarDisposisi() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [disposisiList, setDisposisiList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPenerima, setSelectedPenerima] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [originalFileUrl, setOriginalFileUrl] = useState(null);
  const [userId, setUserId] = useState(null);
  const [tahun, setTahun] = useState(null);
  const [bulan, setBulan] = useState(null);
  const [dinasId, setDinasId] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedDisposisi, setSelectedDisposisi] = useState(null);
  const [editSuratList, setEditSuratList] = useState([]);
  const [editSelectedSurat, setEditSelectedSurat] = useState(null);
  const [editPenerimaOptions, setEditPenerimaOptions] = useState([]);
  const [editSelectedPenerima, setEditSelectedPenerima] = useState([]);
  const [editCatatan, setEditCatatan] = useState("");
  const [editTingkatPerintah, setEditTingkatPerintah] = useState("");
  const [editFiles, setEditFiles] = useState([]);
  const [editTotalSize, setEditTotalSize] = useState(0);
  const [penerimaFilterType, setPenerimaFilterType] = useState(null); // 'langsung' | 'lanjutan' | null (semua)
  const [selectedDisposisiForDialog, setSelectedDisposisiForDialog] =
    useState(null);

  const [statusUser, setStatusUser] = useState(null);
  const [editShowSizeWarning, setEditShowSizeWarning] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const editDropzoneRef = useRef(null);
  const [statistik, setStatistik] = useState({
    total_disposisi: 0,
    total_proses: 0,
    total_selesai: 0,
    total_belum_dibaca: 0,
  });
  const [openChatDialog, setOpenChatDialog] = useState(false);
  const [selectedChatDisposisi, setSelectedChatDisposisi] = useState(null);
  const [selectedChatPenerima, setSelectedChatPenerima] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loadingChat, setLoadingChat] = useState(false);
  const [refreshingChat, setRefreshingChat] = useState(false);
  const chatEndRef = useRef(null);
  const navigate = useNavigate();
  // State untuk dialog respon dan rating
  const [openResponDialog, setOpenResponDialog] = useState(false);
  const [openRatingDialog, setOpenRatingDialog] = useState(false);
  const [selectedPenerimaForRespon, setSelectedPenerimaForRespon] =
    useState(null);
  const [selectedRespon, setSelectedRespon] = useState("");
  const [selectedRating, setSelectedRating] = useState(0);
  const [submittingRespon, setSubmittingRespon] = useState(false);
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setUserId(user.uid);
      setDinasId(user.dinas_id);
      setStatusUser(user.status);
    } else {
      logoutUser(navigate);
    }
    // Ambil tahun dan bulan saat ini (current year dan month) dan set ke state
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11

    setTahun(currentYear);
    setBulan(currentMonth);
  }, []);

  useEffect(() => {
    if (userId && tahun) {
      // Jika bulan belum di-set, set ke bulan saat ini
      if (!bulan) {
        const currentMonth = new Date().getMonth() + 1;
        setBulan(currentMonth);
        return;
      }
      fetchDisposisi();
      fetchStatistik();
    }
  }, [userId, tahun, bulan]);

  // Event handler untuk memvalidasi file di edit dropzone
  useEffect(() => {
    if (!openEditDialog) return;

    const dz = editDropzoneRef.current?.dropzone;
    if (!dz) return;

    const handleAccept = (file, done) => {
      const existingFilesSize = dz.files.reduce((sum, f) => {
        return sum + (f.size || 0);
      }, 0);

      const totalSize = existingFilesSize + file.size;
      const maxTotalSize = 1 * 1024 * 1024; // 1 MB
      const totalSizeMB = totalSize / (1024 * 1024);

      if (totalSize > maxTotalSize) {
        done(`Total ukuran melebihi 1 MB (${totalSizeMB.toFixed(2)} MB)`);
        swal({
          title: "Ukuran File Melebihi Batas",
          text: `Total ukuran semua file (${totalSizeMB.toFixed(
            2
          )} MB) melebihi batas maksimal 1 MB.\n\nSilakan hapus beberapa file yang sudah ada atau pilih file yang lebih kecil.`,
          icon: "error",
          button: "OK",
        });
        return;
      }

      done(null);
    };

    const handleAddedFiles = (files) => {
      setTimeout(() => {
        const allFiles = dz.files;
        const totalSize = allFiles.reduce((sum, file) => {
          return sum + (file.size || 0);
        }, 0);
        const maxTotalSize = 1 * 1024 * 1024; // 1 MB
        const totalSizeMB = totalSize / (1024 * 1024);

        if (totalSize > maxTotalSize) {
          files.forEach((file) => {
            if (dz.files.includes(file)) {
              dz.emit("error", file, `Total ukuran melebihi 1 MB`);
              dz.removeFile(file);
            }
          });

          swal({
            title: "Ukuran File Melebihi Batas",
            text: `Total ukuran semua file (${totalSizeMB.toFixed(
              2
            )} MB) melebihi batas maksimal 1 MB.\n\nSilakan hapus beberapa file yang sudah ada atau pilih file yang lebih kecil.`,
            icon: "error",
            button: "OK",
          });
        }
      }, 0);
    };

    dz.on("accept", handleAccept);
    dz.on("addedfiles", handleAddedFiles);

    return () => {
      if (dz) {
        dz.off("accept", handleAccept);
        dz.off("addedfiles", handleAddedFiles);
      }
    };
  }, [openEditDialog]);

  const fetchDisposisi = async () => {
    try {
      // Jika bulan adalah "semua" atau 0, tidak kirim parameter bulan
      const bulanParam =
        bulan && bulan !== "semua" && bulan !== "0" ? `&bulan=${bulan}` : "";
      const response = await axios.get(
        `${API_ESA_OFFICE}disposisi/disposisi-pembuat/${userId}?tahun=${tahun}${bulanParam}`
      );
      const processedData = response.data.map((item) => ({
        ...item,
        file_surat: item.file_surat
          ? `${API_ESA_OFFICE_UPLOAD}/uploads/${item.file_surat}`
          : null,
        penerima: item.penerima.map((p) => ({
          ...p,
          profil: p.profil ? `${API_ESA_OFFICE_UPLOAD}${p.profil}` : null,
          // Proses profil asal perintah menggunakan uid_RSS
          profil_asal_perintah: p.uid_RSS_asal_perintah
            ? `https://smartdh.rotendaokab.go.id/uploads/${p.uid_RSS_asal_perintah}.jpg`
            : p.profil_asal_perintah
            ? `${API_ESA_OFFICE_UPLOAD}${p.profil_asal_perintah}`
            : null,
        })),
      }));
      setDisposisiList(processedData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching disposisi:", error);
      setLoading(false);
    }
  };

  const fetchStatistik = async () => {
    try {
      // Jika bulan adalah "semua" atau 0, tidak kirim parameter bulan
      const bulanParam =
        bulan && bulan !== "semua" && bulan !== "0" ? `&bulan=${bulan}` : "";
      const response = await axios.get(
        `${API_ESA_OFFICE}disposisi/disposisi-pembuat/${userId}/statistik?tahun=${tahun}${bulanParam}`
      );
      setStatistik(response.data);
    } catch (error) {
      console.error("Error fetching statistik:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Fungsi untuk mendapatkan icon berdasarkan jenis file
  const getFileIcon = (filename) => {
    if (!filename) return <Icon>insert_drive_file</Icon>;

    const extension = filename.split(".").pop()?.toLowerCase();

    switch (extension) {
      case "pdf":
        return <Icon sx={{ color: "#f44336" }}>picture_as_pdf</Icon>;
      case "doc":
      case "docx":
        return <Icon sx={{ color: "#2196f3" }}>description</Icon>;
      case "xls":
      case "xlsx":
        return <Icon sx={{ color: "#4caf50" }}>table_chart</Icon>;
      case "ppt":
      case "pptx":
        return <Icon sx={{ color: "#ff9800" }}>slideshow</Icon>;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return <Icon sx={{ color: "#9c27b0" }}>image</Icon>;
      case "txt":
        return <Icon sx={{ color: "#607d8b" }}>text_snippet</Icon>;
      default:
        return <Icon>insert_drive_file</Icon>;
    }
  };

  const handleOpenPenerimaDialog = (
    penerima,
    disposisiItem = null,
    filterType = null
  ) => {
    setSelectedPenerima(penerima);
    setSelectedDisposisiForDialog(disposisiItem);
    setPenerimaFilterType(filterType); // 'langsung' | 'lanjutan' | null (semua)
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPenerima(null);
    setSelectedDisposisiForDialog(null);
    setPenerimaFilterType(null);
  };

  const handleOpenPdfDialog = (fileUrl) => {
    // Simpan URL asli untuk download dan buka di tab baru
    setOriginalFileUrl(fileUrl);
    // Set URL untuk iframe (langsung, tidak menggunakan Google Docs Viewer)
    setSelectedFile(fileUrl);
    setOpenFileDialog(true);
  };

  const handleCloseFileDialog = () => {
    setOpenFileDialog(false);
    setSelectedFile(null);
    setOriginalFileUrl(null);
  };

  const handleOpenEditDialog = async (disposisi) => {
    setSelectedDisposisi(disposisi);

    // Fetch surat list dan penerima options
    if (dinasId) {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();

      try {
        // Fetch surat list berdasarkan tahun (semua bulan dalam tahun)
        // Hanya ambil surat yang belum didisposisi untuk edit
        const suratResponse = await axios.get(
          `${API_ESA_OFFICE}surat-masuk/dinas/${dinasId}?tahun=${currentYear}&belum_disposisi=true`
        );
        const formattedSurat = suratResponse.data.map((surat) => ({
          ...surat,
          label: `${surat.nomor_surat} - ${surat.perihal}`,
          value: surat.surat_id,
        }));

        // Set selected surat
        const foundSurat = formattedSurat.find(
          (s) => s.surat_id === disposisi.surat_id
        );

        // Jika surat yang sedang diedit tidak ditemukan di list (karena sudah didisposisi),
        // fetch surat tersebut secara khusus dan tambahkan ke list
        if (!foundSurat && disposisi.surat_id) {
          try {
            const suratDetailResponse = await axios.get(
              `${API_ESA_OFFICE}surat-masuk/${disposisi.surat_id}`
            );
            const suratDetail = {
              ...suratDetailResponse.data,
              label: `${suratDetailResponse.data.nomor_surat} - ${suratDetailResponse.data.perihal}`,
              value: suratDetailResponse.data.surat_id,
            };
            formattedSurat.push(suratDetail);
            setEditSuratList(formattedSurat);
            setEditSelectedSurat(suratDetail);
          } catch (error) {
            console.error("Error fetching surat detail:", error);
            setEditSuratList(formattedSurat);
          }
        } else {
          setEditSuratList(formattedSurat);
          if (foundSurat) {
            setEditSelectedSurat(foundSurat);
          }
        }

        // Fetch penerima options
        const penerimaResponse = await axios.get(
          statusUser === "pimpinan"
            ? `${API_ESA_OFFICE}users/pimpinan/list`
            : `${API_ESA_OFFICE}users/dinas/${dinasId}`
        );
        const options = penerimaResponse.data.map((user) => ({
          label: user.nama,
          value: user.user_id,
          avatar: user.uid_RSS
            ? `https://smartdh.rotendaokab.go.id/uploads/${user.uid_RSS}.jpg`
            : undefined,
        }));
        setEditPenerimaOptions(options);

        // Set selected penerima
        if (disposisi.penerima && disposisi.penerima.length > 0) {
          const selectedPenerima = disposisi.penerima.map((p) => {
            const found = options.find((opt) => opt.value === p.penerima_id);
            return found || { label: p.nama, value: p.penerima_id };
          });
          setEditSelectedPenerima(selectedPenerima);
        }

        // Set other fields
        setEditCatatan(disposisi.catatan_disposisi || "");
        // Tingkat perintah dari database adalah angka (1, 2, 3)
        const tingkatPerintahValue = disposisi.penerima?.[0]?.tingkat_perintah;
        setEditTingkatPerintah(
          tingkatPerintahValue ? tingkatPerintahValue.toString() : "1"
        );
        setEditFiles([]);
        setEditTotalSize(0);
        setEditShowSizeWarning(false);
        setOpenEditDialog(true);
      } catch (error) {
        console.error("Error fetching data for edit:", error);
        swal({
          icon: "error",
          title: "Error",
          text: "Gagal memuat data untuk edit",
          button: "OK",
        });
      }
    }
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedDisposisi(null);
    setEditSelectedSurat(null);
    setEditSelectedPenerima([]);
    setEditCatatan("");
    setEditTingkatPerintah("");
    setEditFiles([]);
    setEditTotalSize(0);
    setEditShowSizeWarning(false);
    if (editDropzoneRef.current && editDropzoneRef.current.dropzone) {
      const dz = editDropzoneRef.current.dropzone;
      const filesToRemove = [...dz.files];
      filesToRemove.forEach((file) => {
        dz.removeFile(file);
      });
    }
  };

  const handleEditDropzoneChange = (files) => {
    const totalSizeInMB =
      files.reduce((acc, file) => acc + file.size, 0) / (1024 * 1024);
    const maxSizeMB = 1; // Batas maksimal 1 MB

    setEditTotalSize(totalSizeInMB);
    setEditShowSizeWarning(totalSizeInMB > maxSizeMB);

    if (totalSizeInMB <= maxSizeMB) {
      setEditFiles(files);
    }
  };

  const handleSubmitEdit = async () => {
    if (
      !editSelectedSurat ||
      editSelectedPenerima.length === 0 ||
      !editCatatan ||
      !editTingkatPerintah
    ) {
      swal({
        title: "Data Tidak Lengkap",
        text: "Mohon lengkapi semua data yang diperlukan",
        icon: "warning",
        button: "OK",
      });
      return;
    }

    // Validasi total ukuran file (maksimal 1 MB)
    const totalSizeInMB =
      editFiles.reduce((acc, file) => acc + file.size, 0) / (1024 * 1024);
    if (totalSizeInMB > 1) {
      swal({
        title: "Ukuran File Terlalu Besar!",
        text: `Total ukuran file (${totalSizeInMB.toFixed(
          2
        )} MB) melebihi batas maksimal (1 MB). File tidak dapat disimpan.`,
        icon: "error",
        button: "OK",
      });
      return;
    }

    setEditLoading(true);
    const formData = new FormData();

    formData.append("surat_id", editSelectedSurat.value.toString());
    formData.append("dari_id", userId.toString());
    formData.append("tanggal_disposisi", selectedDisposisi.tanggal_disposisi);
    formData.append("catatan_disposisi", editCatatan);
    formData.append("tingkat_perintah", editTingkatPerintah);

    const penerimaIds = editSelectedPenerima.map((p) => p.value.toString());
    formData.append("penerima", JSON.stringify(penerimaIds));

    if (editFiles.length > 0) {
      editFiles.forEach((file) => {
        formData.append("file_disposisi", file);
      });
    }

    try {
      const response = await axios.put(
        `${API_ESA_OFFICE}disposisi/${selectedDisposisi.disposisi_id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data) {
        swal({
          icon: "success",
          title: "Berhasil",
          text: "Disposisi berhasil diupdate",
          button: "OK",
        });
        handleCloseEditDialog();
        fetchDisposisi();
        fetchStatistik();
      }
    } catch (error) {
      console.error("Error updating disposisi:", error);
      const errorMessage =
        error.response?.data?.message || "Gagal mengupdate disposisi";
      swal({
        icon: "error",
        title: "Error",
        text: errorMessage,
        button: "OK",
      });
    } finally {
      setEditLoading(false);
    }
  };

  const handleOpenDeleteDialog = (disposisi) => {
    setSelectedDisposisi(disposisi);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedDisposisi(null);
  };

  const handleDelete = async () => {
    if (!selectedDisposisi) return;

    try {
      const response = await axios.delete(
        `${API_ESA_OFFICE}disposisi/${selectedDisposisi.disposisi_id}`
      );

      if (response.data) {
        swal({
          icon: "success",
          title: "Berhasil",
          text: "Disposisi berhasil dihapus",
          button: "OK",
        });
        handleCloseDeleteDialog();
        fetchDisposisi();
        fetchStatistik();
      }
    } catch (error) {
      console.error("Error deleting disposisi:", error);
      const errorMessage =
        error.response?.data?.message || "Gagal menghapus disposisi";
      swal({
        icon: "error",
        title: "Error",
        text: errorMessage,
        button: "OK",
      });
    }
  };

  const getFreshToken = async (user) => {
    if (!user?.uid_RSS) {
      throw new Error("User uid_RSS tidak ditemukan");
    }

    try {
      const tokenResponse = await axios.get(
        `${API_ESA_OFFICE}users/uid/${user.uid_RSS}`,
        {
          withCredentials: true, // Kirim cookie untuk refresh token
        }
      );

      if (tokenResponse.data?.accessToken) {
        const token = tokenResponse.data.accessToken;
        // Simpan token ke localStorage
        const updatedUser = { ...user, accessToken: token };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        return token;
      }
      throw new Error("Access token tidak ditemukan di response");
    } catch (error) {
      console.error("Error getting fresh token:", error);

      // Handle error khusus untuk status_EO tidak aktif
      if (error.response && error.response.status === 403) {
        const errorData = error.response.data;
        const errorMessage =
          errorData.reason || errorData.message || "Akun Anda tidak aktif.";

        // Clear localStorage dan logout user
        localStorage.removeItem("user");
        localStorage.removeItem("daftarHadir");

        swal({
          title: "Akses Ditolak",
          text: errorMessage,
          icon: "error",
          button: "OK",
        }).then(() => {
          window.location.href = "/authentication/sign-in/cover";
        });

        throw new Error(errorMessage);
      }

      throw error;
    }
  };

  // Fungsi untuk fetch chat history
  const fetchChatHistory = async (disposisiItem, penerima, token) => {
    const disposisiId = disposisiItem.disposisi_id;
    const disposisiPenerimaId = penerima?.id || null;

    // Fetch chat history
    let response;
    try {
      const url = disposisiPenerimaId
        ? `${API_CHAT}disposisi/${disposisiId}?disposisi_penerima_id=${disposisiPenerimaId}`
        : `${API_CHAT}disposisi/${disposisiId}`;
      response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });
    } catch (error) {
      // Jika 403, coba refresh token sekali lagi
      if (error.response?.status === 403) {
        console.log("🔄 Token mungkin expired, refreshing...");
        try {
          const user = JSON.parse(localStorage.getItem("user")) || {};
          token = await getFreshToken(user);
          const url = disposisiPenerimaId
            ? `${API_CHAT}disposisi/${disposisiId}?disposisi_penerima_id=${disposisiPenerimaId}`
            : `${API_CHAT}disposisi/${disposisiId}`;
          response = await axios.get(url, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          });
        } catch (retryError) {
          console.error(
            "❌ Chat API Error after retry:",
            retryError.response?.data || retryError.message
          );
          throw retryError;
        }
      } else {
        throw error;
      }
    }

    // Set chat messages
    if (response && response.data && response.data.success) {
      const messages = response.data.data || [];
      setChatMessages(messages);

      // Auto mark all messages as read
      try {
        const user = JSON.parse(localStorage.getItem("user")) || {};
        const currentUserId =
          userId || user.uid_RSS || user.user_id || user.uid;
        const markReadUrl = disposisiPenerimaId
          ? `${API_CHAT}disposisi/${disposisiId}/read-all?disposisi_penerima_id=${disposisiPenerimaId}`
          : `${API_CHAT}disposisi/${disposisiId}/read-all`;
        const markReadResponse = await axios.put(
          markReadUrl,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );
        if (markReadResponse.data.success) {
          console.log("✅ All messages marked as read");
          setChatMessages((prev) =>
            prev.map((msg) => {
              if (!msg.waktu_baca && msg.penerima_id === currentUserId) {
                return { ...msg, waktu_baca: new Date().toISOString() };
              }
              return msg;
            })
          );
        }
      } catch (markReadError) {
        console.warn("⚠️ Failed to mark messages as read:", markReadError);
      }
    }

    return response;
  };

  // Handler untuk refresh chat messages
  const handleRefreshChat = async () => {
    if (!selectedChatDisposisi || refreshingChat) {
      return;
    }

    setRefreshingChat(true);
    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      if (!user.uid_RSS) {
        swal("Error", "Data user tidak lengkap. Silakan login ulang.", "error");
        setRefreshingChat(false);
        return;
      }

      const token = await getFreshToken(user);
      await fetchChatHistory(
        selectedChatDisposisi,
        selectedChatPenerima,
        token
      );

      // Scroll to bottom after refresh
      setTimeout(() => {
        if (chatEndRef.current) {
          chatEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 150);
    } catch (error) {
      console.error("Error refreshing chat:", error);
      swal("Error", "Gagal refresh chat. Silakan coba lagi.", "error");
    } finally {
      setRefreshingChat(false);
    }
  };

  // Handler untuk membuka chat
  const handleOpenChat = async (disposisiItem, penerima) => {
    setSelectedChatDisposisi(disposisiItem);
    setSelectedChatPenerima(penerima);
    setOpenChatDialog(true);
    setLoadingChat(true);
    setChatMessages([]);
    setNewMessage("");

    try {
      // Ambil user dari localStorage
      const user = JSON.parse(localStorage.getItem("user")) || {};

      if (!user.uid_RSS) {
        swal("Error", "Data user tidak lengkap. Silakan login ulang.", "error");
        setLoadingChat(false);
        return;
      }

      // Selalu dapatkan fresh token untuk memastikan tidak expired
      let token;
      try {
        token = await getFreshToken(user);
        console.log("✅ Fresh token obtained");
      } catch (tokenError) {
        console.error("Error getting fresh token:", tokenError);
        swal(
          "Error",
          "Gagal mendapatkan token akses. Silakan login ulang.",
          "error"
        );
        setLoadingChat(false);
        return;
      }

      if (!token) {
        swal("Error", "Token tidak ditemukan. Silakan login ulang.", "error");
        setLoadingChat(false);
        return;
      }

      // Fetch chat history
      try {
        await fetchChatHistory(disposisiItem, penerima, token);
        // Scroll to bottom
        setTimeout(() => {
          if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 150);
      } catch (error) {
        console.error("Error loading chat:", error);
        if (error.response?.status >= 400) {
          swal(
            "Error",
            error.response?.data?.error || "Gagal memuat chat",
            "error"
          );
        }
        setChatMessages([]);
      }
    } catch (error) {
      console.error("Error loading chat:", error);
      swal("Error", "Gagal memuat chat. Silakan coba lagi.", "error");
    } finally {
      setLoadingChat(false);
    }
  };

  // Handler untuk menutup chat
  const handleCloseChat = () => {
    setOpenChatDialog(false);
    setSelectedChatDisposisi(null);
    setSelectedChatPenerima(null);
    setChatMessages([]);
    setNewMessage("");
  };

  // Handler untuk mengirim pesan
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChatDisposisi || !selectedChatPenerima) {
      return;
    }

    const messageText = newMessage.trim();
    setNewMessage("");

    const currentUserId =
      userId ||
      JSON.parse(localStorage.getItem("user"))?.uid ||
      JSON.parse(localStorage.getItem("user"))?.user_id;
    const tempId = `temp-${Date.now()}-${Math.random()}`;

    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};

      if (!user.uid_RSS) {
        swal("Error", "Data user tidak lengkap. Silakan login ulang.", "error");
        setNewMessage(messageText); // Restore message on error
        return;
      }

      // Dapatkan fresh token
      let token;
      try {
        token = await getFreshToken(user);
      } catch (tokenError) {
        console.error("Error getting fresh token:", tokenError);
        swal(
          "Error",
          "Gagal mendapatkan token akses. Silakan login ulang.",
          "error"
        );
        setNewMessage(messageText); // Restore message on error
        return;
      }

      if (!token) {
        swal("Error", "Token tidak ditemukan. Silakan login ulang.", "error");
        setNewMessage(messageText); // Restore message on error
        return;
      }

      const tempMessage = {
        id_chat: tempId,
        pesan: messageText,
        pengirim_id: currentUserId,
        pengirim_nama: user.nama || "Anda",
        pengirim_profil: user.uid_RSS || user.uid_RSS,
        waktu_kirim: new Date().toISOString(),
        isSending: true,
      };

      setChatMessages((prev) => {
        const exists = prev.some((msg) => msg.id_chat === tempId);
        if (exists) return prev;
        return [...prev, tempMessage];
      });

      setTimeout(() => {
        if (chatEndRef.current) {
          chatEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 150);

      const messageData = {
        disposisi_id: selectedChatDisposisi.disposisi_id,
        disposisi_penerima_id: selectedChatPenerima?.id || null,
        penerima_id: selectedChatPenerima.penerima_id,
        pesan: messageText,
      };

      const response = await axios.post(`${API_CHAT}disposisi`, messageData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (response.data && response.data.success) {
        // Hapus pesan temporary
        setChatMessages((prev) => prev.filter((msg) => msg.id_chat !== tempId));

        // Refresh chat untuk mendapatkan pesan terbaru
        await fetchChatHistory(
          selectedChatDisposisi,
          selectedChatPenerima,
          token
        );

        // Scroll to bottom
        setTimeout(() => {
          if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 150);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setChatMessages((prev) => prev.filter((msg) => msg.id_chat !== tempId));
      setNewMessage(messageText);
      swal(
        "Error",
        error.response?.data?.error || "Gagal mengirim pesan",
        "error"
      );
    }
  };

  // Handler untuk delete message
  const handleDeleteMessage = async (chatId) => {
    if (!chatId) return;

    const confirmed = await swal({
      title: "Hapus Pesan?",
      text: "Pesan yang dihapus tidak dapat dikembalikan.",
      icon: "warning",
      buttons: {
        cancel: {
          text: "Batal",
          value: false,
          visible: true,
        },
        confirm: {
          text: "Hapus",
          value: true,
          visible: true,
        },
      },
      dangerMode: true,
    });

    if (!confirmed) return;

    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      const token = await getFreshToken(user);

      await axios.delete(`${API_CHAT}disposisi/${chatId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });

      setChatMessages((prev) => prev.filter((msg) => msg.id_chat !== chatId));
      swal("Berhasil", "Pesan berhasil dihapus", "success");
    } catch (error) {
      console.error("Error deleting message:", error);
      swal("Error", "Gagal menghapus pesan", "error");
    }
  };

  // Format waktu untuk chat
  const formatChatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);

    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const timeStr = `${hours}:${minutes}`;

    if (diffDays === 0) {
      return timeStr;
    }

    if (diffDays === 1) {
      return `Kemarin ${timeStr}`;
    }

    return `${date.getDate()}/${date.getMonth() + 1} ${timeStr}`;
  };

  // Handler untuk dialog respon pemberi disposisi
  const handleOpenResponDialog = (penerima) => {
    setSelectedPenerimaForRespon(penerima);
    setSelectedRespon(penerima.respon_pemberi_disposisi || "");
    // Set rating jika sudah ada dan respon adalah Terima
    if (penerima.respon_pemberi_disposisi === "Terima" && penerima.nilai) {
      setSelectedRating(penerima.nilai);
    } else {
      setSelectedRating(0);
    }
    setOpenResponDialog(true);
  };

  const handleCloseResponDialog = () => {
    setOpenResponDialog(false);
    setSelectedPenerimaForRespon(null);
    setSelectedRespon("");
    setSelectedRating(0);
  };

  // Handler untuk memilih respon
  const handleSelectRespon = (respon) => {
    setSelectedRespon(respon);
    if (respon === "Terima") {
      // Jika sedang edit dan sudah ada rating, pertahankan rating
      // Jika belum ada rating, reset ke 0
      if (
        !selectedPenerimaForRespon?.respon_pemberi_disposisi ||
        selectedPenerimaForRespon?.respon_pemberi_disposisi !== "Terima" ||
        !selectedPenerimaForRespon?.nilai
      ) {
        setSelectedRating(0);
      }
      // Tutup dialog respon dan buka dialog rating
      setOpenResponDialog(false);
      setOpenRatingDialog(true);
    } else {
      // Jika memilih Perbaiki atau Tolak, reset rating
      setSelectedRating(0);
    }
  };

  // Handler untuk submit respon
  const handleSubmitRespon = async () => {
    if (!selectedPenerimaForRespon || !selectedPenerimaForRespon.id) {
      swal({
        title: "Error",
        text: "Data penerima tidak valid",
        icon: "error",
        button: "OK",
      });
      return;
    }

    if (!selectedRespon) {
      swal({
        title: "Perhatian",
        text: "Silakan pilih respon terlebih dahulu",
        icon: "warning",
        button: "OK",
      });
      return;
    }

    // Jika Terima, harus ada rating
    if (selectedRespon === "Terima" && selectedRating === 0) {
      swal({
        title: "Perhatian",
        text: "Silakan pilih penilaian terlebih dahulu",
        icon: "warning",
        button: "OK",
      });
      return;
    }

    setSubmittingRespon(true);
    try {
      const payload = {
        respon_pemberi_disposisi: selectedRespon,
        nilai: selectedRespon === "Terima" ? selectedRating : null,
      };

      await axios.put(
        `${API_ESA_OFFICE}disposisi/respon/${selectedPenerimaForRespon.id}`,
        payload
      );

      swal({
        title: "Berhasil!",
        text:
          selectedRespon === "Terima"
            ? "Respon 'Terima' dan penilaian berhasil disimpan"
            : `Respon '${selectedRespon}' berhasil disimpan. Status diubah menjadi Proses.`,
        icon: "success",
        button: "OK",
      });

      // Refresh data utama dan update dialog
      const disposisiIdForRefresh = selectedDisposisiForDialog?.disposisi_id;
      const isDialogOpen = openDialog;

      fetchDisposisi().then(() => {
        // Update data di dialog jika dialog masih terbuka
        if (isDialogOpen && disposisiIdForRefresh) {
          // Fetch ulang data disposisi
          const bulanParam =
            bulan && bulan !== "semua" && bulan !== "0"
              ? `&bulan=${bulan}`
              : "";
          axios
            .get(
              `${API_ESA_OFFICE}disposisi/disposisi-pembuat/${userId}?tahun=${tahun}${bulanParam}`
            )
            .then((response) => {
              const processedData = response.data.map((item) => ({
                ...item,
                file_surat: item.file_surat
                  ? `${API_ESA_OFFICE_UPLOAD}/uploads/${item.file_surat}`
                  : null,
                penerima: item.penerima.map((p) => ({
                  ...p,
                  profil: p.profil
                    ? `${API_ESA_OFFICE_UPLOAD}${p.profil}`
                    : null,
                  profil_asal_perintah: p.uid_RSS_asal_perintah
                    ? `https://smartdh.rotendaokab.go.id/uploads/${p.uid_RSS_asal_perintah}.jpg`
                    : p.profil_asal_perintah
                    ? `${API_ESA_OFFICE_UPLOAD}${p.profil_asal_perintah}`
                    : null,
                })),
              }));

              const updatedDisposisi = processedData.find(
                (item) => item.disposisi_id === disposisiIdForRefresh
              );
              if (updatedDisposisi && updatedDisposisi.penerima) {
                setSelectedPenerima(updatedDisposisi.penerima);
              }
            })
            .catch((error) => {
              console.error("Error refreshing data:", error);
            });
        }
      });

      handleCloseResponDialog();
      setOpenRatingDialog(false);
      setSelectedRating(0);
      setSelectedRespon("");
    } catch (error) {
      console.error("Error submitting respon:", error);
      swal({
        title: "Error",
        text:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Gagal mengirimkan respon",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSubmittingRespon(false);
    }
  };

  // Handler untuk rating click
  const handleRatingClick = (rating) => {
    setSelectedRating(rating);
  };

  // Handler untuk submit rating (dari dialog rating)
  const handleSubmitRating = async () => {
    if (selectedRating === 0) {
      swal({
        title: "Perhatian",
        text: "Silakan pilih penilaian terlebih dahulu",
        icon: "warning",
        button: "OK",
      });
      return;
    }

    if (!selectedPenerimaForRespon || !selectedPenerimaForRespon.id) {
      swal({
        title: "Error",
        text: "Data penerima tidak valid",
        icon: "error",
        button: "OK",
      });
      return;
    }

    setSubmittingRespon(true);
    try {
      const payload = {
        respon_pemberi_disposisi: "Terima",
        nilai: selectedRating,
      };

      await axios.put(
        `${API_ESA_OFFICE}disposisi/respon/${selectedPenerimaForRespon.id}`,
        payload
      );

      swal({
        title: "Berhasil!",
        text: "Respon 'Terima' dan penilaian berhasil disimpan",
        icon: "success",
        button: "OK",
      });

      // Refresh data utama dan update dialog
      const disposisiIdForRefresh = selectedDisposisiForDialog?.disposisi_id;
      const isDialogOpen = openDialog;

      fetchDisposisi().then(() => {
        // Update data di dialog jika dialog masih terbuka
        if (isDialogOpen && disposisiIdForRefresh) {
          // Fetch ulang data disposisi
          const bulanParam =
            bulan && bulan !== "semua" && bulan !== "0"
              ? `&bulan=${bulan}`
              : "";
          axios
            .get(
              `${API_ESA_OFFICE}disposisi/disposisi-pembuat/${userId}?tahun=${tahun}${bulanParam}`
            )
            .then((response) => {
              const processedData = response.data.map((item) => ({
                ...item,
                file_surat: item.file_surat
                  ? `${API_ESA_OFFICE_UPLOAD}/uploads/${item.file_surat}`
                  : null,
                penerima: item.penerima.map((p) => ({
                  ...p,
                  profil: p.profil
                    ? `${API_ESA_OFFICE_UPLOAD}${p.profil}`
                    : null,
                  profil_asal_perintah: p.uid_RSS_asal_perintah
                    ? `https://smartdh.rotendaokab.go.id/uploads/${p.uid_RSS_asal_perintah}.jpg`
                    : p.profil_asal_perintah
                    ? `${API_ESA_OFFICE_UPLOAD}${p.profil_asal_perintah}`
                    : null,
                })),
              }));

              const updatedDisposisi = processedData.find(
                (item) => item.disposisi_id === disposisiIdForRefresh
              );
              if (updatedDisposisi && updatedDisposisi.penerima) {
                setSelectedPenerima(updatedDisposisi.penerima);
              }
            })
            .catch((error) => {
              console.error("Error refreshing data:", error);
            });
        }
      });

      setOpenRatingDialog(false);
      setSelectedRating(0);
      setSelectedRespon("");
      setSelectedPenerimaForRespon(null);
    } catch (error) {
      console.error("Error submitting respon:", error);
      swal({
        title: "Error",
        text:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Gagal mengirimkan respon",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSubmittingRespon(false);
    }
  };

  // Handler untuk cancel rating dialog
  const handleCancelRating = async () => {
    const cancelResult = await swal({
      title: "Konfirmasi",
      text: "Apakah Anda yakin ingin membatalkan? Penilaian wajib diisi untuk respon 'Terima'.",
      icon: "warning",
      buttons: ["Tidak", "Ya, Batalkan"],
      dangerMode: true,
    });

    if (cancelResult) {
      setOpenRatingDialog(false);
      setSelectedRating(0);
      setSelectedRespon("");
      setSelectedPenerimaForRespon(null);
    }
  };

  // Fungsi untuk menghitung progress disposisi
  // Hanya menghitung penerima langsung (asal_perintah_dari_id === userId)
  const calculateProgress = (penerima, pengirimId) => {
    if (!penerima || penerima.length === 0) return 0;

    // Filter hanya penerima langsung
    const penerimaLangsung = penerima.filter(
      (p) => p.asal_perintah_dari_id === pengirimId
    );

    if (penerimaLangsung.length === 0) return 0;

    const totalPenerima = penerimaLangsung.length;
    const selesaiCount = penerimaLangsung.filter(
      (p) =>
        p.waktu_selesai && p.waktu_selesai !== null && p.waktu_selesai !== ""
    ).length;

    return totalPenerima > 0
      ? Math.round((selesaiCount / totalPenerima) * 100)
      : 0;
  };

  // Fungsi untuk mendapatkan status disposisi
  // Hanya menghitung penerima langsung (asal_perintah_dari_id === userId)
  const getDisposisiStatus = (penerima, pengirimId) => {
    if (!penerima || penerima.length === 0) return "pending";

    // Filter hanya penerima langsung
    const penerimaLangsung = penerima.filter(
      (p) => p.asal_perintah_dari_id === pengirimId
    );

    if (penerimaLangsung.length === 0) return "pending";

    const totalPenerima = penerimaLangsung.length;
    const selesaiCount = penerimaLangsung.filter(
      (p) =>
        p.waktu_selesai && p.waktu_selesai !== null && p.waktu_selesai !== ""
    ).length;

    if (selesaiCount === 0) return "pending";
    if (selesaiCount === totalPenerima) return "completed";
    return "in_progress";
  };

  // Render penerima langsung
  const renderPenerimaLangsung = (penerima, disposisiItem) => {
    if (!penerima || penerima.length === 0) {
      return (
        <MDTypography variant="caption" color="text.secondary">
          -
        </MDTypography>
      );
    }

    const penerimaLangsung = penerima.filter(
      (p) => p.asal_perintah_dari_id === userId
    );

    if (penerimaLangsung.length === 0) {
      return (
        <MDTypography variant="caption" color="text.secondary">
          -
        </MDTypography>
      );
    }

    if (penerimaLangsung.length === 1) {
      return (
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDBox
            display="flex"
            alignItems="center"
            onClick={() =>
              handleOpenPenerimaDialog(penerima, disposisiItem, "langsung")
            }
            sx={{ cursor: "pointer" }}
          >
            <MDAvatar
              src={penerimaLangsung[0].profil}
              alt={penerimaLangsung[0].nama}
              size="sm"
              sx={{ mr: 1 }}
            />
            <MDTypography variant="button" fontWeight="medium">
              {penerimaLangsung[0].nama}
            </MDTypography>
          </MDBox>
          {/* Icon Chat - tampil di sebelah nama jika hanya 1 penerima */}
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenChat(disposisiItem, penerimaLangsung[0]);
            }}
            sx={{ ml: 0.5 }}
            title="Chat dengan penerima"
          >
            <ChatIcon fontSize="small" />
          </IconButton>
        </MDBox>
      );
    }

    return (
      <MDBox display="flex" alignItems="center" gap={1}>
        <Button
          variant="contained"
          size="small"
          color="primary"
          onClick={() =>
            handleOpenPenerimaDialog(penerima, disposisiItem, "langsung")
          }
        >
          {penerimaLangsung.length} Penerima
        </Button>
        {/* Icon Chat - tampil di sebelah tombol jika lebih dari 1 penerima, buka dialog daftar penerima disposisi dulu */}
        <IconButton
          size="small"
          color="primary"
          onClick={(e) => {
            e.stopPropagation();
            // Jika lebih dari 1 penerima, buka dialog daftar penerima disposisi dulu untuk memilih penerima yang ingin diajak chat
            handleOpenPenerimaDialog(penerima, disposisiItem, "langsung");
          }}
          sx={{ ml: 0.5 }}
          title="Chat dengan penerima"
        >
          <ChatIcon fontSize="small" />
        </IconButton>
      </MDBox>
    );
  };

  // Render penerima lanjutan
  const renderPenerimaLanjutan = (penerima, disposisiItem) => {
    if (!penerima || penerima.length === 0) {
      return (
        <MDTypography variant="caption" color="text.secondary">
          -
        </MDTypography>
      );
    }

    const penerimaLanjutan = penerima.filter(
      (p) => p.asal_perintah_dari_id !== userId
    );

    if (penerimaLanjutan.length === 0) {
      return (
        <MDTypography variant="caption" color="text.secondary">
          -
        </MDTypography>
      );
    }

    if (penerimaLanjutan.length === 1) {
      return (
        <MDBox
          display="flex"
          alignItems="center"
          onClick={() =>
            handleOpenPenerimaDialog(penerima, disposisiItem, "lanjutan")
          }
          sx={{ cursor: "pointer" }}
        >
          <MDAvatar
            src={penerimaLanjutan[0].profil}
            alt={penerimaLanjutan[0].nama}
            size="sm"
            sx={{ mr: 1 }}
          />
          <MDTypography variant="button" fontWeight="medium">
            {penerimaLanjutan[0].nama}
          </MDTypography>
        </MDBox>
      );
    }

    return (
      <Button
        variant="outlined"
        size="small"
        sx={{
          backgroundColor: "#ffffff",
          borderColor: "#000000",
          color: "#000000",
          "&:hover": {
            backgroundColor: "#f5f5f5",
            borderColor: "#000000",
          },
        }}
        onClick={() =>
          handleOpenPenerimaDialog(penerima, disposisiItem, "lanjutan")
        }
      >
        {penerimaLanjutan.length} Penerima
      </Button>
    );
  };

  const renderFileButton = (file) => {
    if (!file) {
      return <MDTypography variant="caption">Tidak ada file</MDTypography>;
    }

    // Pastikan file URL lengkap dengan path yang benar
    const fileUrl = file.startsWith("http")
      ? file
      : file.startsWith("/uploads/")
      ? `${API_ESA_OFFICE_UPLOAD}${file}`
      : `${API_ESA_OFFICE_UPLOAD}/uploads/disposisi/${file}`;

    return (
      <Button
        variant="contained"
        size="small"
        startIcon={<VisibilityIcon />}
        onClick={() => handleOpenPdfDialog(fileUrl)}
      >
        Lihat
      </Button>
    );
  };

  // Render progress bar untuk kolom progress
  // Hanya menghitung penerima langsung
  const renderProgress = (penerima) => {
    const progress = calculateProgress(penerima, userId);
    const status = getDisposisiStatus(penerima, userId);

    // Filter hanya penerima langsung untuk perhitungan
    const penerimaLangsung =
      penerima?.filter((p) => p.asal_perintah_dari_id === userId) || [];

    const selesaiCount = penerimaLangsung.filter(
      (p) =>
        p.waktu_selesai && p.waktu_selesai !== null && p.waktu_selesai !== ""
    ).length;

    let color = "info";
    if (status === "completed") color = "success";
    else if (status === "pending") color = "warning";

    return (
      <MDBox>
        <MDProgress
          value={progress}
          color={color}
          variant="gradient"
          sx={{ mb: 1 }}
        />
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {progress}% ({selesaiCount}/{penerimaLangsung.length})
        </MDTypography>
      </MDBox>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Daftar Disposisi
                </MDTypography>
              </MDBox>
              <MDBox pt={4} pb={3} px={3}>
                {/* Filter Tahun dan Bulan */}
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      options={(() => {
                        const currentYear = new Date().getFullYear();
                        const years = [];
                        // Mulai dari 2025 sampai current tahun
                        for (let i = 2025; i <= currentYear; i++) {
                          years.push({ label: i.toString(), value: i });
                        }
                        // Urutkan dari terbaru ke terlama (DESC)
                        return years.reverse();
                      })()}
                      value={
                        tahun ? { label: tahun.toString(), value: tahun } : null
                      }
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setTahun(newValue.value);
                        }
                      }}
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Tahun"
                          variant="standard"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      options={[
                        { label: "Semua Bulan", value: "semua" },
                        { label: "Januari", value: "1" },
                        { label: "Februari", value: "2" },
                        { label: "Maret", value: "3" },
                        { label: "April", value: "4" },
                        { label: "Mei", value: "5" },
                        { label: "Juni", value: "6" },
                        { label: "Juli", value: "7" },
                        { label: "Agustus", value: "8" },
                        { label: "September", value: "9" },
                        { label: "Oktober", value: "10" },
                        { label: "November", value: "11" },
                        { label: "Desember", value: "12" },
                      ]}
                      value={
                        bulan
                          ? bulan === "semua"
                            ? { label: "Semua Bulan", value: "semua" }
                            : {
                                label: [
                                  "Januari",
                                  "Februari",
                                  "Maret",
                                  "April",
                                  "Mei",
                                  "Juni",
                                  "Juli",
                                  "Agustus",
                                  "September",
                                  "Oktober",
                                  "November",
                                  "Desember",
                                ][parseInt(bulan) - 1],
                                value: bulan,
                              }
                          : null
                      }
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setBulan(newValue.value);
                        }
                      }}
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Bulan"
                          variant="standard"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  {/* Statistik */}
                  <Grid item xs={12} sm={12} md={6}>
                    <MDBox
                      display="flex"
                      gap={2}
                      flexWrap="wrap"
                      alignItems="center"
                      height="100%"
                    >
                      <Chip
                        label={`Total: ${statistik.total_disposisi || 0}`}
                        color="info"
                        size="medium"
                        sx={{ fontSize: "0.875rem", fontWeight: "medium" }}
                      />
                      <Chip
                        label={`Proses: ${statistik.total_proses || 0}`}
                        color="warning"
                        size="medium"
                        sx={{ fontSize: "0.875rem", fontWeight: "medium" }}
                      />
                      <Chip
                        label={`Selesai: ${statistik.total_selesai || 0}`}
                        color="success"
                        size="medium"
                        sx={{ fontSize: "0.875rem", fontWeight: "medium" }}
                      />
                      <Chip
                        label={`Belum Dibaca: ${
                          statistik.total_belum_dibaca || 0
                        }`}
                        color="error"
                        size="medium"
                        sx={{ fontSize: "0.875rem", fontWeight: "medium" }}
                      />
                    </MDBox>
                  </Grid>
                </Grid>
                {isMobile ? (
                  <MDBox display="flex" flexDirection="column" gap={2} px={2}>
                    {disposisiList.map((disposisi, index) => (
                      <Card key={disposisi.disposisi_id || index}>
                        <CardContent>
                          <MDBox mb={1}>
                            <MDTypography variant="h6" fontWeight="bold">
                              {disposisi.nomor_surat || "-"}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatDate(disposisi.tanggal_disposisi)}
                            </MDTypography>
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Pembuat:
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {disposisi.nama_pengirim || "-"}
                            </MDTypography>
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Perihal:
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {disposisi.perihal || "-"}
                            </MDTypography>
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Catatan:
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {disposisi.catatan_disposisi || "-"}
                            </MDTypography>
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                              mb={0.5}
                            >
                              Progress:
                            </MDTypography>
                            {renderProgress(disposisi.penerima)}
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                              mb={0.5}
                            >
                              Penerima Langsung:
                            </MDTypography>
                            {renderPenerimaLangsung(
                              disposisi.penerima,
                              disposisi
                            )}
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                              mb={0.5}
                            >
                              Penerima Lanjutan:
                            </MDTypography>
                            {renderPenerimaLanjutan(
                              disposisi.penerima,
                              disposisi
                            )}
                          </MDBox>
                          {disposisi.file_surat && (
                            <MDBox mt={1}>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() =>
                                  handleOpenPdfDialog(disposisi.file_surat)
                                }
                              >
                                Lihat File Surat
                              </Button>
                            </MDBox>
                          )}
                          <MDBox mt={2} display="flex" gap={1}>
                            <IconButton
                              color="info"
                              size="small"
                              onClick={() => handleOpenEditDialog(disposisi)}
                              title="Edit"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleOpenDeleteDialog(disposisi)}
                              title="Hapus"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </MDBox>
                        </CardContent>
                      </Card>
                    ))}
                    {disposisiList.length === 0 && !loading && (
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        py={4}
                      >
                        <MDTypography variant="h6" color="text.secondary">
                          Tidak ada data disposisi
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                ) : (
                  <DataTable
                    table={{
                      columns: [
                        {
                          Header: "No",
                          accessor: "no",
                          width: "5%",
                          Cell: ({ row }) => (
                            <MDTypography variant="caption" color="text">
                              {row.index + 1}
                            </MDTypography>
                          ),
                        },
                        {
                          Header: "No. Surat",
                          accessor: "nomor_surat",
                          width: "10%",
                        },
                        {
                          Header: "File Surat",
                          accessor: "file_surat",
                          width: "8%",
                          Cell: ({ value }) =>
                            value ? (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => {
                                  handleOpenPdfDialog(value);
                                }}
                              >
                                Lihat
                              </Button>
                            ) : null,
                        },
                        {
                          Header: "Tanggal Disposisi",
                          accessor: "tanggal_disposisi",
                          width: "10%",
                          Cell: ({ value }) => formatDate(value),
                        },
                        {
                          Header: "Pembuat Disposisi",
                          accessor: "nama_pengirim",
                          width: "12%",
                        },
                        {
                          Header: "Perihal",
                          accessor: "perihal",
                          width: "300px",
                          Cell: ({ value }) => (
                            <MDBox
                              sx={{
                                whiteSpace: "normal",
                                wordWrap: "break-word",
                                maxWidth: "300px",
                                py: 1,
                              }}
                            >
                              <MDTypography variant="caption" color="text">
                                {value || "-"}
                              </MDTypography>
                            </MDBox>
                          ),
                        },
                        {
                          Header: "Catatan",
                          accessor: "catatan_disposisi",
                          width: "300px",
                          Cell: ({ value }) => (
                            <MDBox
                              sx={{
                                whiteSpace: "normal",
                                wordWrap: "break-word",
                                maxWidth: "300px",
                                py: 1,
                              }}
                            >
                              <MDTypography variant="caption" color="text">
                                {value || "-"}
                              </MDTypography>
                            </MDBox>
                          ),
                        },
                        {
                          Header: "Progress Tindak Lanjut",
                          accessor: "progress_tindak_lanjut",
                          width: "12%",
                          Cell: ({ row }) =>
                            renderProgress(row.original.penerima),
                        },
                        {
                          Header: "Penerima Langsung",
                          accessor: "penerima_langsung",
                          width: "12%",
                          Cell: ({ row }) =>
                            renderPenerimaLangsung(
                              row.original.penerima,
                              row.original
                            ),
                        },
                        {
                          Header: "Penerima Lanjutan",
                          accessor: "penerima_lanjutan",
                          width: "12%",
                          Cell: ({ row }) =>
                            renderPenerimaLanjutan(
                              row.original.penerima,
                              row.original
                            ),
                        },
                        {
                          Header: "Aksi",
                          accessor: "aksi",
                          width: "8%",
                          Cell: ({ row }) => (
                            <MDBox display="flex" gap={1}>
                              <IconButton
                                color="info"
                                size="small"
                                onClick={() =>
                                  handleOpenEditDialog(row.original)
                                }
                                title="Edit"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() =>
                                  handleOpenDeleteDialog(row.original)
                                }
                                title="Hapus"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </MDBox>
                          ),
                        },
                      ],
                      rows: disposisiList,
                    }}
                    entriesPerPage={{
                      defaultValue: 10,
                      entries: [5, 10, 15, 20, 25],
                    }}
                    showTotalEntries={true}
                    isSorted={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialog untuk menampilkan daftar penerima dengan Card */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h6" fontWeight="bold" color="dark">
            Daftar Penerima Disposisi
            {penerimaFilterType === "langsung" && " - Penerima Langsung"}
            {penerimaFilterType === "lanjutan" && " - Penerima Lanjutan"}
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox p={2}>
            {/* Penerima Langsung */}
            {(!penerimaFilterType || penerimaFilterType === "langsung") &&
              selectedPenerima?.filter(
                (p) => p.asal_perintah_dari_id === userId
              ).length > 0 && (
                <MDBox mb={3}>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    color="primary"
                    mb={2}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Icon>person</Icon>
                    Penerima Langsung (
                    {
                      selectedPenerima.filter(
                        (p) => p.asal_perintah_dari_id === userId
                      ).length
                    }
                    )
                  </MDTypography>
                  <Grid container spacing={2}>
                    {selectedPenerima
                      .filter((p) => p.asal_perintah_dari_id === userId)
                      .map((penerima) => {
                        const isCompleted =
                          penerima.waktu_selesai &&
                          penerima.waktu_selesai !== null &&
                          penerima.waktu_selesai !== "";
                        const isRead =
                          penerima.waktu_baca && penerima.waktu_baca !== null;

                        // Parse file_TL_disposisi
                        let fileTLArr = [];
                        if (Array.isArray(penerima.file_TL_disposisi)) {
                          fileTLArr = penerima.file_TL_disposisi;
                        } else if (
                          typeof penerima.file_TL_disposisi === "string" &&
                          penerima.file_TL_disposisi.trim().startsWith("[")
                        ) {
                          try {
                            fileTLArr = JSON.parse(penerima.file_TL_disposisi);
                          } catch (e) {
                            fileTLArr = [];
                          }
                        }

                        return (
                          <Grid item xs={12} key={penerima.penerima_id}>
                            <Card>
                              <CardContent>
                                {/* Penerima Disposisi */}
                                <MDBox mb={1}>
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1.5}
                                    mb={1}
                                  >
                                    <MDAvatar
                                      src={penerima.profil}
                                      alt={penerima.nama}
                                      size="sm"
                                    />
                                    <MDBox sx={{ flex: 1 }}>
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        fontWeight="medium"
                                      >
                                        Penerima:
                                      </MDTypography>
                                      <MDBox
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                      >
                                        <MDTypography
                                          variant="body2"
                                          color="text"
                                        >
                                          {penerima.nama}
                                        </MDTypography>
                                        {/* Icon Chat - tampil di sebelah nama penerima */}
                                        {selectedDisposisiForDialog && (
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleCloseDialog();
                                              setTimeout(() => {
                                                handleOpenChat(
                                                  selectedDisposisiForDialog,
                                                  penerima
                                                );
                                              }, 100);
                                            }}
                                            sx={{ ml: 0.5 }}
                                            title="Chat dengan penerima"
                                          >
                                            <ChatIcon fontSize="small" />
                                          </IconButton>
                                        )}
                                      </MDBox>
                                    </MDBox>
                                  </MDBox>
                                </MDBox>

                                {/* Status */}
                                <MDBox mb={1}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight="medium"
                                    mb={0.5}
                                  >
                                    Status:
                                  </MDTypography>
                                  <MDBox display="flex" gap={1} flexWrap="wrap">
                                    {isCompleted ? (
                                      <Chip
                                        icon={<CheckCircleIcon />}
                                        label="Selesai"
                                        color="success"
                                        size="small"
                                      />
                                    ) : isRead ? (
                                      <Chip
                                        icon={<ScheduleIcon />}
                                        label="Dibaca"
                                        color="info"
                                        size="small"
                                      />
                                    ) : (
                                      <Chip
                                        icon={<PendingIcon />}
                                        label="Pending"
                                        color="warning"
                                        size="small"
                                      />
                                    )}
                                  </MDBox>
                                </MDBox>

                                {/* Waktu Dibaca */}
                                {isRead && penerima.waktu_baca && (
                                  <MDBox mb={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      fontWeight="medium"
                                    >
                                      Waktu Dibaca:
                                    </MDTypography>
                                    <MDTypography variant="body2" color="text">
                                      {formatDate(penerima.waktu_baca)}
                                    </MDTypography>
                                  </MDBox>
                                )}

                                {/* Waktu Selesai */}
                                {isCompleted && penerima.waktu_selesai && (
                                  <MDBox mb={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      fontWeight="medium"
                                    >
                                      Waktu Selesai:
                                    </MDTypography>
                                    <MDTypography variant="body2" color="text">
                                      {formatDate(penerima.waktu_selesai)}
                                    </MDTypography>
                                  </MDBox>
                                )}

                                {/* Respon Pemberi Disposisi */}
                                {penerima.respon_pemberi_disposisi && (
                                  <MDBox mb={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      fontWeight="medium"
                                      mb={0.5}
                                    >
                                      Respon:
                                    </MDTypography>
                                    <Chip
                                      label={penerima.respon_pemberi_disposisi}
                                      color={
                                        penerima.respon_pemberi_disposisi ===
                                        "Terima"
                                          ? "success"
                                          : penerima.respon_pemberi_disposisi ===
                                            "Tolak"
                                          ? "error"
                                          : "warning"
                                      }
                                      size="small"
                                      sx={{ mr: 1 }}
                                    />
                                    {penerima.respon_pemberi_disposisi ===
                                      "Terima" &&
                                      penerima.nilai && (
                                        <MDBox
                                          display="flex"
                                          alignItems="center"
                                          gap={0.5}
                                          mt={0.5}
                                        >
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            Rating:
                                          </MDTypography>
                                          {[1, 2, 3, 4, 5].map((star) => (
                                            <Icon
                                              key={star}
                                              sx={{
                                                color:
                                                  star <= penerima.nilai
                                                    ? "#FFD700"
                                                    : "#E0E0E0",
                                                fontSize: "1.2rem",
                                              }}
                                            >
                                              star
                                            </Icon>
                                          ))}
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            ({penerima.nilai}/5)
                                          </MDTypography>
                                        </MDBox>
                                      )}
                                  </MDBox>
                                )}

                                {/* Button untuk Respon (hanya jika status = Selesai dan waktu_selesai terisi, serta asal_perintah_dari_id = userId) */}
                                {isCompleted &&
                                  penerima.waktu_selesai &&
                                  penerima.asal_perintah_dari_id === userId && (
                                    <MDBox mb={1}>
                                      <MDButton
                                        variant={
                                          penerima.respon_pemberi_disposisi
                                            ? "outlined"
                                            : "gradient"
                                        }
                                        color={
                                          penerima.respon_pemberi_disposisi
                                            ? "secondary"
                                            : "info"
                                        }
                                        size="small"
                                        onClick={() =>
                                          handleOpenResponDialog(penerima)
                                        }
                                        startIcon={<Icon>rate_review</Icon>}
                                      >
                                        {penerima.respon_pemberi_disposisi
                                          ? "Ubah Respon"
                                          : "Berikan Respon"}
                                      </MDButton>
                                    </MDBox>
                                  )}

                                {/* File Tindak Lanjut */}
                                <MDBox mb={1}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight="medium"
                                    mb={0.5}
                                  >
                                    File Tindak Lanjut ({fileTLArr.length}):
                                  </MDTypography>
                                  {fileTLArr.length > 0 ? (
                                    <MDBox
                                      display="flex"
                                      flexDirection="column"
                                      gap={1}
                                    >
                                      {fileTLArr.map((file, idx) => {
                                        const filename = file.split("/").pop();
                                        return (
                                          <Button
                                            key={idx}
                                            variant="outlined"
                                            size="small"
                                            startIcon={getFileIcon(filename)}
                                            href={`${API_ESA_OFFICE_UPLOAD}${file}`}
                                            target="_blank"
                                            sx={{
                                              justifyContent: "flex-start",
                                              textTransform: "none",
                                            }}
                                          >
                                            {filename}
                                          </Button>
                                        );
                                      })}
                                    </MDBox>
                                  ) : (
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Tidak ada file
                                    </MDTypography>
                                  )}
                                </MDBox>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                  </Grid>
                </MDBox>
              )}

            {/* Penerima Lanjutan */}
            {(!penerimaFilterType || penerimaFilterType === "lanjutan") &&
              selectedPenerima?.filter(
                (p) => p.asal_perintah_dari_id !== userId
              ).length > 0 && (
                <MDBox>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    color="secondary"
                    mb={2}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Icon>forward</Icon>
                    Penerima Lanjutan (
                    {
                      selectedPenerima.filter(
                        (p) => p.asal_perintah_dari_id !== userId
                      ).length
                    }
                    )
                  </MDTypography>
                  <Grid container spacing={2}>
                    {selectedPenerima
                      .filter((p) => p.asal_perintah_dari_id !== userId)
                      .map((penerima) => {
                        const isCompleted =
                          penerima.waktu_selesai &&
                          penerima.waktu_selesai !== null &&
                          penerima.waktu_selesai !== "";
                        const isRead =
                          penerima.waktu_baca && penerima.waktu_baca !== null;

                        // Parse file_TL_disposisi
                        let fileTLArr = [];
                        if (Array.isArray(penerima.file_TL_disposisi)) {
                          fileTLArr = penerima.file_TL_disposisi;
                        } else if (
                          typeof penerima.file_TL_disposisi === "string" &&
                          penerima.file_TL_disposisi.trim().startsWith("[")
                        ) {
                          try {
                            fileTLArr = JSON.parse(penerima.file_TL_disposisi);
                          } catch (e) {
                            fileTLArr = [];
                          }
                        }

                        return (
                          <Grid item xs={12} key={penerima.penerima_id}>
                            <Card
                              sx={{
                                border: "2px solid #9c27b0",
                                borderRadius: 1.5,
                                overflow: "hidden",
                                background: "#ffffff",
                                boxShadow: "0 2px 8px rgba(156, 39, 176, 0.1)",
                              }}
                            >
                              <CardContent>
                                {/* Asal Perintah */}
                                {penerima.nama_asal_perintah && (
                                  <MDBox mb={2}>
                                    <MDBox
                                      display="flex"
                                      alignItems="center"
                                      gap={1.5}
                                      mb={1}
                                    >
                                      <MDAvatar
                                        src={penerima.profil_asal_perintah}
                                        alt={penerima.nama_asal_perintah}
                                        size="sm"
                                      />
                                      <MDBox>
                                        <MDTypography
                                          variant="caption"
                                          color="text.secondary"
                                          fontWeight="medium"
                                        >
                                          Dari:
                                        </MDTypography>
                                        <MDTypography
                                          variant="body2"
                                          color="text"
                                        >
                                          {penerima.nama_asal_perintah}
                                        </MDTypography>
                                      </MDBox>
                                    </MDBox>
                                    <MDBox
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                    >
                                      <ArrowForwardIcon
                                        sx={{
                                          fontSize: 20,
                                          color: "text.secondary",
                                        }}
                                      />
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Penerima:
                                      </MDTypography>
                                    </MDBox>
                                  </MDBox>
                                )}

                                {/* Penerima Disposisi */}
                                <MDBox mb={1}>
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1.5}
                                    mb={1}
                                  >
                                    <MDAvatar
                                      src={penerima.profil}
                                      alt={penerima.nama}
                                      size="sm"
                                    />
                                    <MDBox sx={{ flex: 1 }}>
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        fontWeight="medium"
                                      >
                                        Penerima:
                                      </MDTypography>
                                      <MDBox
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                      >
                                        <MDTypography
                                          variant="body2"
                                          color="text"
                                        >
                                          {penerima.nama}
                                        </MDTypography>
                                        {/* Icon Chat - tampil di sebelah nama penerima */}
                                        {selectedDisposisiForDialog && (
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleCloseDialog();
                                              setTimeout(() => {
                                                handleOpenChat(
                                                  selectedDisposisiForDialog,
                                                  penerima
                                                );
                                              }, 100);
                                            }}
                                            sx={{ ml: 0.5 }}
                                            title="Chat dengan penerima"
                                          >
                                            <ChatIcon fontSize="small" />
                                          </IconButton>
                                        )}
                                      </MDBox>
                                    </MDBox>
                                  </MDBox>
                                </MDBox>

                                {/* Status */}
                                <MDBox mb={1}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight="medium"
                                    mb={0.5}
                                  >
                                    Status:
                                  </MDTypography>
                                  <MDBox display="flex" gap={1} flexWrap="wrap">
                                    {isCompleted ? (
                                      <Chip
                                        icon={<CheckCircleIcon />}
                                        label="Selesai"
                                        color="success"
                                        size="small"
                                      />
                                    ) : isRead ? (
                                      <Chip
                                        icon={<ScheduleIcon />}
                                        label="Dibaca"
                                        color="info"
                                        size="small"
                                      />
                                    ) : (
                                      <Chip
                                        icon={<PendingIcon />}
                                        label="Pending"
                                        color="warning"
                                        size="small"
                                      />
                                    )}
                                  </MDBox>
                                </MDBox>

                                {/* Waktu Dibaca */}
                                {isRead && penerima.waktu_baca && (
                                  <MDBox mb={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      fontWeight="medium"
                                    >
                                      Waktu Dibaca:
                                    </MDTypography>
                                    <MDTypography variant="body2" color="text">
                                      {formatDate(penerima.waktu_baca)}
                                    </MDTypography>
                                  </MDBox>
                                )}

                                {/* Waktu Selesai */}
                                {isCompleted && penerima.waktu_selesai && (
                                  <MDBox mb={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      fontWeight="medium"
                                    >
                                      Waktu Selesai:
                                    </MDTypography>
                                    <MDTypography variant="body2" color="text">
                                      {formatDate(penerima.waktu_selesai)}
                                    </MDTypography>
                                  </MDBox>
                                )}

                                {/* File Tindak Lanjut */}
                                <MDBox mb={1}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight="medium"
                                    mb={0.5}
                                  >
                                    File Tindak Lanjut ({fileTLArr.length}):
                                  </MDTypography>
                                  {fileTLArr.length > 0 ? (
                                    <MDBox
                                      display="flex"
                                      flexDirection="column"
                                      gap={1}
                                    >
                                      {fileTLArr.map((file, idx) => {
                                        const filename = file.split("/").pop();
                                        return (
                                          <Button
                                            key={idx}
                                            variant="outlined"
                                            size="small"
                                            startIcon={getFileIcon(filename)}
                                            href={`${API_ESA_OFFICE_UPLOAD}${file}`}
                                            target="_blank"
                                            sx={{
                                              justifyContent: "flex-start",
                                              textTransform: "none",
                                            }}
                                          >
                                            {filename}
                                          </Button>
                                        );
                                      })}
                                    </MDBox>
                                  ) : (
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Tidak ada file
                                    </MDTypography>
                                  )}
                                </MDBox>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                  </Grid>
                </MDBox>
              )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog untuk menampilkan file PDF */}
      <Dialog
        open={openFileDialog}
        onClose={handleCloseFileDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDBox display="flex" alignItems="center">
              <PictureAsPdfIcon sx={{ mr: 1, color: "error.main" }} />
              <MDTypography variant="h6">File Surat</MDTypography>
            </MDBox>
            <IconButton onClick={handleCloseFileDialog} size="small">
              <CloseIcon />
            </IconButton>
          </MDBox>
          <MDBox mt={2} display="flex" justifyContent="flex-start" gap={2}>
            <Button
              variant="outlined"
              onClick={() => {
                if (originalFileUrl) {
                  window.open(originalFileUrl, "_blank", "noopener,noreferrer");
                }
              }}
              sx={{
                borderColor: "#000000",
                color: "#000000",
                "&:hover": {
                  borderColor: "#000000",
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Buka di Tab Baru
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                if (originalFileUrl) {
                  const link = document.createElement("a");
                  link.href = originalFileUrl;
                  link.download = originalFileUrl.split("/").pop();
                  link.click();
                }
              }}
              sx={{
                borderColor: "#000000",
                color: "#000000",
                "&:hover": {
                  borderColor: "#000000",
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Download
            </Button>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {selectedFile ? (
              <iframe
                src={selectedFile}
                width="100%"
                height="600px"
                title="PDF Viewer"
                style={{ border: "none" }}
                onError={(e) => {
                  console.error("Error loading PDF:", e);
                  swal({
                    icon: "error",
                    title: "Oops...",
                    text: "Gagal memuat file PDF",
                    button: "OK",
                  });
                }}
              />
            ) : (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="600px"
              >
                <MDTypography variant="h6" color="text">
                  File tidak tersedia
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseFileDialog}
            variant="contained"
            color="error"
            startIcon={<CloseIcon />}
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Edit Disposisi */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h6">Edit Disposisi</MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Autocomplete
                  options={editSuratList}
                  getOptionLabel={(option) => option.label || ""}
                  value={editSelectedSurat}
                  onChange={(event, newValue) => setEditSelectedSurat(newValue)}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      label="Pilih Surat"
                      variant="standard"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={editPenerimaOptions}
                  value={editSelectedPenerima}
                  onChange={(event, newValue) =>
                    setEditSelectedPenerima(newValue)
                  }
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      label="Pilih Penerima Disposisi"
                      variant="standard"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.value}
                        label={option.label}
                        avatar={
                          option.avatar ? (
                            <img
                              src={option.avatar}
                              alt={option.label}
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                              }}
                            />
                          ) : null
                        }
                      />
                    ))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  label="Tingkat Perintah"
                  value={editTingkatPerintah}
                  onChange={(e) => setEditTingkatPerintah(e.target.value)}
                  fullWidth
                  variant="standard"
                >
                  <MenuItem value="1">Segera</MenuItem>
                  <MenuItem value="2">Biasa</MenuItem>
                  <MenuItem value="3">Penting</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Catatan Disposisi"
                  multiline
                  rows={4}
                  fullWidth
                  value={editCatatan}
                  onChange={(e) => setEditCatatan(e.target.value)}
                  variant="standard"
                />
              </Grid>
              <Grid item xs={12}>
                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontWeight="regular"
                  >
                    * Maksimal total ukuran file: 1 MB
                    <br />* Format yang didukung: PDF, DOC, DOCX
                  </MDTypography>
                </MDBox>
                {useMemo(
                  () => (
                    <MDDropzone
                      options={{
                        addRemoveLinks: true,
                        uploadMultiple: true,
                        maxFilesize: 1,
                        maxFiles: 5,
                        maxTotalFilesize: 1,
                        acceptedFiles: ".pdf,.doc,.docx",
                        dictDefaultMessage:
                          "Klik atau seret file ke sini untuk mengunggah",
                        dictTotalFilesTooBig:
                          "Total ukuran file melebihi batas maksimal (1 MB)",
                        dictInvalidFileType:
                          "Format file tidak didukung. Hanya PDF dan DOC/DOCX yang diperbolehkan",
                      }}
                      onChange={handleEditDropzoneChange}
                      ref={editDropzoneRef}
                    />
                  ),
                  []
                )}
                {editShowSizeWarning && (
                  <MDBox mt={2}>
                    <MDTypography
                      variant="caption"
                      color="error"
                      fontWeight="regular"
                    >
                      ⚠️ Total ukuran file ({editTotalSize.toFixed(2)} MB)
                      melebihi batas maksimal (1 MB). File tidak dapat disimpan.
                    </MDTypography>
                  </MDBox>
                )}
                <MDBox mt={1}>
                  <MDTypography
                    variant="caption"
                    color={editTotalSize > 1 ? "error" : "text"}
                    fontWeight="regular"
                  >
                    Total ukuran: {editTotalSize.toFixed(2)} MB / 1 MB
                  </MDTypography>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={editLoading}>
            Batal
          </Button>
          <Button
            onClick={handleSubmitEdit}
            variant="contained"
            color="info"
            disabled={editLoading || editShowSizeWarning || editTotalSize > 1}
          >
            {editLoading ? "Menyimpan..." : "Simpan"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Konfirmasi Delete */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h6">Konfirmasi Hapus</MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            <MDTypography variant="body1">
              Apakah Anda yakin ingin menghapus disposisi ini?
            </MDTypography>
            {selectedDisposisi && (
              <MDBox mt={2}>
                <MDTypography variant="body2" color="text.secondary">
                  No. Surat: {selectedDisposisi.nomor_surat}
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary">
                  Perihal: {selectedDisposisi.perihal}
                </MDTypography>
              </MDBox>
            )}
            <MDBox mt={2}>
              <MDTypography variant="caption" color="error">
                ⚠️ Tindakan ini tidak dapat dibatalkan. Semua data terkait
                (termasuk disposisi_penerima) akan dihapus.
              </MDTypography>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Batal</Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Hapus
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Chat */}
      <Dialog
        open={openChatDialog}
        onClose={handleCloseChat}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            height: "80vh",
            maxHeight: "600px",
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        {/* Header - Material Dashboard Style */}
        <MDBox
          sx={{
            bgcolor: "info.main",
            color: "white",
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: 2,
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1.5}>
            <MDAvatar
              src={selectedChatPenerima?.profil || undefined}
              alt={selectedChatPenerima?.nama}
              size="sm"
              sx={{ border: "2px solid rgba(255,255,255,0.3)" }}
            />
            <MDBox>
              <MDTypography variant="h6" fontWeight="bold" color="white">
                {selectedChatPenerima?.nama || "Penerima"}
              </MDTypography>
              <MDTypography variant="caption" color="white">
                {selectedChatDisposisi?.perihal || "Disposisi"}
              </MDTypography>
            </MDBox>
          </MDBox>
          <MDBox display="flex" gap={1}>
            <IconButton
              onClick={handleRefreshChat}
              disabled={refreshingChat || loadingChat}
              size="small"
              sx={{
                bgcolor: "white",
                color: refreshingChat ? "grey.500" : "grey.700",
                "&:hover": {
                  bgcolor: "grey.100",
                },
                "&:disabled": {
                  bgcolor: "grey.300",
                  color: "grey.500",
                },
              }}
              title="Refresh Chat"
            >
              {refreshingChat ? (
                <CircularProgress size={20} sx={{ color: "grey.500" }} />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
            <IconButton
              onClick={handleCloseChat}
              size="small"
              sx={{
                bgcolor: "white",
                color: "grey.700",
                "&:hover": {
                  bgcolor: "grey.100",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </MDBox>
        </MDBox>
        <DialogContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            p: 0,
          }}
        >
          {loadingChat ? (
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <CircularProgress />
            </MDBox>
          ) : (
            <MDBox
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {chatMessages.length === 0 ? (
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                >
                  <MDTypography variant="body2" color="text.secondary">
                    Belum ada pesan. Mulai percakapan!
                  </MDTypography>
                </MDBox>
              ) : (
                chatMessages.map((message, idx) => {
                  const currentUser =
                    JSON.parse(localStorage.getItem("user")) || {};
                  const currentUserId =
                    userId || currentUser.uid || currentUser.user_id;
                  const isOwnMessage = message.pengirim_id === currentUserId;
                  const isSending = message.isSending;

                  return (
                    <MDBox
                      key={message.id_chat || idx}
                      display="flex"
                      justifyContent={isOwnMessage ? "flex-end" : "flex-start"}
                      sx={{ mb: 0.5, px: 0.5 }}
                    >
                      <MDBox
                        sx={{
                          maxWidth: "75%",
                          minWidth: "100px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: isOwnMessage ? "flex-end" : "flex-start",
                        }}
                      >
                        {!isOwnMessage && (
                          <MDBox
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            mb={0.3}
                            sx={{ px: 0.5 }}
                          >
                            <MDAvatar
                              src={
                                message.pengirim_profil
                                  ? `https://smartdh.rotendaokab.go.id/uploads/${message.pengirim_profil}.jpg`
                                  : undefined
                              }
                              size="xs"
                            />
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                            >
                              {message.pengirim_nama || "-"}
                            </MDTypography>
                          </MDBox>
                        )}

                        <MDBox
                          sx={{
                            p: 1.25,
                            px: 1.5,
                            borderRadius: isOwnMessage
                              ? "7.5px 0 7.5px 7.5px"
                              : "0 7.5px 7.5px 7.5px",
                            backgroundColor: isOwnMessage ? "#4caf50" : "white",
                            color: isOwnMessage ? "white" : "text.primary",
                            wordBreak: "break-word",
                            boxShadow: 1,
                            position: "relative",
                            opacity: isSending ? 0.7 : 1,
                            border: isOwnMessage ? "none" : "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <MDTypography
                            variant="body2"
                            sx={{
                              color: isOwnMessage ? "white" : "text.primary",
                              fontSize: "0.95rem",
                              lineHeight: 1.4,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {message.pesan}
                            {isSending && (
                              <MDBox
                                component="span"
                                sx={{
                                  display: "inline-block",
                                  ml: 0.5,
                                  verticalAlign: "middle",
                                }}
                              >
                                <CircularProgress
                                  size={12}
                                  sx={{ color: "white" }}
                                />
                              </MDBox>
                            )}
                          </MDTypography>
                        </MDBox>

                        <MDBox
                          display="flex"
                          alignItems="center"
                          gap={0.5}
                          sx={{ mt: 0.2, px: 0.5 }}
                        >
                          <MDTypography
                            variant="caption"
                            sx={{
                              fontSize: "0.65rem",
                              color: "#4caf50",
                              fontWeight: 400,
                            }}
                          >
                            {formatChatTime(message.waktu_kirim)}{" "}
                          </MDTypography>
                          {isOwnMessage && !isSending && (
                            <MDBox
                              sx={{
                                width: "12px",
                                height: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <CheckCircleIcon
                                sx={{
                                  fontSize: "12px",
                                  color: message.waktu_baca
                                    ? "rgba(11, 183, 25, 0.7)"
                                    : "rgba(255,255,255,0.6)",
                                }}
                              />
                            </MDBox>
                          )}
                          {isOwnMessage && message.waktu_baca && (
                            <MDTypography
                              variant="caption"
                              sx={{
                                fontSize: "0.6rem",
                                color: "rgba(11, 183, 25, 0.7)",
                                fontStyle: "italic",
                              }}
                            >
                              Dibaca {formatChatTime(message.waktu_baca)}
                            </MDTypography>
                          )}
                          {isOwnMessage && !isSending && (
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDeleteMessage(message.id_chat)
                              }
                              sx={{
                                width: "20px",
                                height: "20px",
                                color: "rgba(251, 27, 15, 0.7)",
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: "14px" }} />
                            </IconButton>
                          )}
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </MDBox>
          )}
        </DialogContent>
        <MDBox
          sx={{
            bgcolor: "grey.50",
            p: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "flex-end",
            gap: 1,
          }}
        >
          <TextField
            fullWidth
            placeholder="Ketik pesan..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            multiline
            maxRows={4}
            size="small"
            disabled={loadingChat}
            sx={{
              bgcolor: "white",
              borderRadius: "20px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                "& fieldset": {
                  borderColor: "rgba(0,0,0,0.1)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(0,0,0,0.2)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#000000",
                },
              },
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || loadingChat}
            sx={{
              bgcolor: "#000000",
              color: "white",
              width: "40px",
              height: "40px",
              "&:hover": {
                bgcolor: "#333333",
              },
              "&:disabled": {
                bgcolor: "#cccccc",
              },
            }}
          >
            <SendIcon />
          </IconButton>
        </MDBox>
      </Dialog>

      {/* Dialog Respon Pemberi Disposisi */}
      <Dialog
        open={openResponDialog}
        onClose={handleCloseResponDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h6" fontWeight="bold" color="dark">
            Berikan Respon Disposisi
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox p={2}>
            <MDTypography variant="body2" color="text" mb={2}>
              Penerima: {selectedPenerimaForRespon?.nama || "-"}
            </MDTypography>
            <MDTypography variant="body2" color="text" mb={2}>
              Silakan pilih respon Anda terhadap disposisi yang telah
              diselesaikan:
            </MDTypography>
            <MDBox display="flex" flexDirection="column" gap={1}>
              <MDButton
                variant={selectedRespon === "Terima" ? "gradient" : "outlined"}
                color={selectedRespon === "Terima" ? "success" : "secondary"}
                onClick={() => handleSelectRespon("Terima")}
                startIcon={<Icon>check_circle</Icon>}
                fullWidth
              >
                Terima
              </MDButton>
              <MDButton
                variant={selectedRespon === "Tolak" ? "gradient" : "outlined"}
                color={selectedRespon === "Tolak" ? "error" : "secondary"}
                onClick={() => handleSelectRespon("Tolak")}
                startIcon={<Icon>cancel</Icon>}
                fullWidth
              >
                Tolak
              </MDButton>
              <MDButton
                variant={
                  selectedRespon === "Perbaiki" ? "gradient" : "outlined"
                }
                color={selectedRespon === "Perbaiki" ? "warning" : "secondary"}
                onClick={() => handleSelectRespon("Perbaiki")}
                startIcon={<Icon>build</Icon>}
                fullWidth
              >
                Perbaiki
              </MDButton>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={handleCloseResponDialog}
          >
            Batal
          </MDButton>
          <MDButton
            variant="gradient"
            color="success"
            onClick={handleSubmitRespon}
            disabled={!selectedRespon || submittingRespon}
            startIcon={<Icon>send</Icon>}
          >
            {submittingRespon ? "Mengirim..." : "Kirim"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog Rating Bintang */}
      <Dialog
        open={openRatingDialog}
        onClose={() => {}}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          <MDTypography variant="h5" color="text" fontWeight="bold">
            Penilaian Layanan
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox textAlign="center" py={2}>
            <MDTypography variant="body2" color="text" mb={2}>
              Silakan berikan penilaian Anda terhadap disposisi yang telah
              diselesaikan:
            </MDTypography>
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              flexWrap="wrap"
              sx={{
                py: 3,
              }}
            >
              {[1, 2, 3, 4, 5].map((rating) => (
                <MDBox
                  key={rating}
                  onClick={() => handleRatingClick(rating)}
                  sx={{
                    cursor: "pointer",
                    width: "75px",
                    height: "75px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": {
                      transform: "scale(1.1)",
                      transition: "transform 0.2s",
                    },
                    transition: "transform 0.2s",
                    "@media (max-width: 600px)": {
                      width: "50px",
                      height: "50px",
                    },
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="60"
                    height="60"
                    fill={selectedRating >= rating ? "#FFD700" : "#E0E0E0"}
                    style={{
                      transition: "fill 0.2s",
                    }}
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </MDBox>
              ))}
            </MDBox>
            {selectedRating > 0 && (
              <MDTypography
                variant="body2"
                color="text.secondary"
                mt={2}
                sx={{ fontStyle: "italic" }}
              >
                Anda memberikan {selectedRating} bintang
                {selectedRating === 1
                  ? " (Sangat Tidak Puas)"
                  : selectedRating === 2
                  ? " (Tidak Puas)"
                  : selectedRating === 3
                  ? " (Cukup)"
                  : selectedRating === 4
                  ? " (Puas)"
                  : " (Sangat Puas)"}
              </MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={handleCancelRating}
          >
            Batal
          </MDButton>
          <MDButton
            variant="gradient"
            color="success"
            onClick={handleSubmitRating}
            disabled={selectedRating === 0 || submittingRespon}
            startIcon={<Icon>send</Icon>}
          >
            {submittingRespon ? "Mengirim..." : "Kirim"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default DaftarDisposisi;
